import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getDriverOnboardingStatus,
  getDriverTraining,
  getDriverDocuments,
  getDriverPolicies,
  markDriverReadyToDrive,
  rejectDriver,
  verifyDriverDocument,
  downloadComplianceExport,
  waiveTrainingAssignment,
  waiveDriverPolicy,
  getDriverActivity,
} from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

const STEP_LABELS = {
  DOCS_UPLOAD: "Docs Upload",
  BACKGROUND_PENDING: "Background Check",
  TRAINING_IN_PROGRESS: "Training",
  DRUG_TEST_PENDING: "Drug Test",
  FINAL_REVIEW: "Final Review",
  READY_TO_DRIVE: "Ready to Drive",
  REJECTED: "Rejected",
};

const STATUS_COLORS = {
  ASSIGNED: { bg: "#fef3c7", fg: "#92400e" },
  IN_PROGRESS: { bg: "#dbeafe", fg: "#1e40af" },
  PASSED: { bg: "#dcfce7", fg: "#166534" },
  FAILED_RETAKE_REQUIRED: { bg: "#fee2e2", fg: "#991b1b" },
  EXPIRED: { bg: "#fee2e2", fg: "#991b1b" },
  WAIVED: { bg: "#e0e7ff", fg: "#3730a3" },
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || { bg: "#e2e8f0", fg: "#475569" };
  return (
    <Chip
      label={status.replace(/_/g, " ")}
      size="small"
      sx={{
        backgroundColor: c.bg,
        color: c.fg,
        fontWeight: 600,
      }}
    />
  );
};

export const DriverCompliancePage = () => {
  const { driverId } = useParams();
  const navigate = useNavigate();

  const [progress, setProgress] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, t, d, pol, act] = await Promise.all([
        getDriverOnboardingStatus(driverId),
        getDriverTraining(driverId),
        getDriverDocuments(driverId),
        getDriverPolicies(driverId),
        getDriverActivity(driverId),
      ]);
      setProgress(unwrapObject(p));
      setTrainings(unwrapList(t));
      setDocuments(unwrapList(d));
      setPolicies(unwrapList(pol));
      setActivity(unwrapList(act));
    } catch (e) {
      setError(e?.message || "Failed to load driver compliance data");
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReady = async () => {
    try {
      await markDriverReadyToDrive(driverId);
      await load();
    } catch (e) {
      setError(e?.message || "Failed to mark ready to drive");
    }
  };

  const handleReject = async () => {
    try {
      await rejectDriver(driverId, rejectReason);
      setRejectOpen(false);
      setRejectReason("");
      await load();
    } catch (e) {
      setError(e?.message || "Failed to reject driver");
    }
  };

  const handleVerifyDoc = async (docId) => {
    try {
      await verifyDriverDocument(docId);
      await load();
    } catch (e) {
      setError(e?.message || "Failed to verify document");
    }
  };

  if (loading && !progress) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 8, minHeight: 400 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isReady = !!progress?.readyToDriveAt;
  const isRejected = progress?.currentStep === "REJECTED";

  return (
    <Stack
      spacing={3}
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Button onClick={() => navigate(-1)} sx={{ mb: 1 }}>
            ← Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Driver Compliance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Driver ID: {driverId}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={async () => {
              try {
                await downloadComplianceExport(driverId);
              } catch (e) {
                setError(e?.message || "Export failed");
              }
            }}
            variant="outlined"
          >
            Audit Packet
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            variant="outlined"
          >
            Refresh
          </Button>
          {!isReady && !isRejected ? (
            <>
              <Button
                startIcon={<CheckCircleIcon />}
                onClick={handleReady}
                variant="contained"
                sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
              >
                Mark Ready to Drive
              </Button>
              <Button
                startIcon={<BlockIcon />}
                onClick={() => setRejectOpen(true)}
                variant="outlined"
                color="error"
              >
                Reject
              </Button>
            </>
          ) : null}
        </Box>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {/* Progress card */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Onboarding Progress
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 2 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: "#1F4E78" }}>
              {progress?.completionPercent || 0}%
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {STEP_LABELS[progress?.currentStep] || progress?.currentStep}
            </Typography>
          </Box>
          {progress?.startedAt ? (
            <Typography variant="body2" color="text.secondary">
              Started{" "}
              {new Date(progress.startedAt).toLocaleDateString()}
            </Typography>
          ) : null}
          {isReady ? (
            <Chip
              label={`Ready to drive — cleared ${new Date(progress.readyToDriveAt).toLocaleDateString()}`}
              sx={{
                backgroundColor: "#dcfce7",
                color: "#166534",
                fontWeight: 600,
                mt: 1,
              }}
            />
          ) : null}
          {isRejected ? (
            <Box sx={{ mt: 1 }}>
              <Chip
                label="Rejected"
                sx={{ backgroundColor: "#fee2e2", color: "#991b1b", fontWeight: 600 }}
              />
              {progress?.rejectionReason ? (
                <Typography variant="body2" sx={{ mt: 1, color: "#7f1d1d" }}>
                  Reason: {progress.rejectionReason}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* Trainings */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Training Assignments ({trainings.length})
          </Typography>
          {trainings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No trainings assigned.
            </Typography>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {trainings.map((t) => {
                const canWaive = t.status !== "PASSED" && t.status !== "WAIVED";
                return (
                  <Box
                    key={t.id}
                    sx={{
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {t.moduleTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.moduleCode}
                        {t.expiresAt
                          ? ` · Expires ${new Date(t.expiresAt).toLocaleDateString()}`
                          : ""}
                        {t.dueBy && t.status !== "PASSED"
                          ? ` · Due ${new Date(t.dueBy).toLocaleDateString()}`
                          : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <StatusPill status={t.status} />
                      {canWaive ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={async () => {
                            const reason = window.prompt(
                              `Waive "${t.moduleTitle}" for this driver?\n\nEnter the reason for the waiver (required — recorded in the audit trail):`,
                              "",
                            );
                            if (!reason || !reason.trim()) return;
                            try {
                              await waiveTrainingAssignment(t.id, reason.trim());
                              load();
                            } catch (e) {
                              setError(e?.response?.data?.message || e?.message || "Waive failed");
                            }
                          }}
                        >
                          Waive
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Documents */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Documents ({documents.length})
          </Typography>
          {documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No documents uploaded.
            </Typography>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {documents.map((d) => (
                <Box
                  key={d.id}
                  sx={{
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {d.documentName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.documentType}
                      {d.uploadedAt
                        ? ` · Uploaded ${new Date(d.uploadedAt).toLocaleDateString()}`
                        : ""}
                      {d.expiresAt
                        ? ` · Expires ${new Date(d.expiresAt).toLocaleDateString()}`
                        : ""}
                    </Typography>
                  </Box>
                  {d.verifiedAt ? (
                    <Chip
                      label="Verified"
                      size="small"
                      sx={{ backgroundColor: "#dcfce7", color: "#166534" }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleVerifyDoc(d.id)}
                    >
                      Verify
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Policies */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Policy acknowledgments ({policies.length})
          </Typography>
          {policies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No policies required.
            </Typography>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {policies.map((p) => {
                const PILL = {
                  SIGNED:  { bg: "#dcfce7", fg: "#166534", label: "Signed" },
                  WAIVED:  { bg: "#e0e7ff", fg: "#3730a3", label: "Waived" },
                  EXPIRED: { bg: "#fee2e2", fg: "#991b1b", label: "Expired" },
                  REVOKED: { bg: "#e2e8f0", fg: "#475569", label: "Revoked" },
                };
                const pill = PILL[p.status] || { bg: "#fef3c7", fg: "#92400e", label: "Pending" };
                const canWaive = p.status !== "SIGNED" && p.status !== "WAIVED";
                return (
                  <Box
                    key={p.id}
                    sx={{
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {p.policyTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        v{p.policyVersion}
                        {p.signedAt
                          ? ` · ${p.status === "WAIVED" ? "Waived" : "Signed"} ${new Date(p.signedAt).toLocaleDateString()}`
                          : ""}
                        {p.typedName ? ` as "${p.typedName}"` : ""}
                        {p.expiresAt
                          ? ` · Expires ${new Date(p.expiresAt).toLocaleDateString()}`
                          : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip
                        label={pill.label}
                        size="small"
                        sx={{ backgroundColor: pill.bg, color: pill.fg, fontWeight: 600 }}
                      />
                      {canWaive ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={async () => {
                            const reason = window.prompt(
                              `Waive "${p.policyTitle}" for this driver?\n\nEnter the reason (required — recorded in audit trail):`,
                              "",
                            );
                            if (!reason || !reason.trim()) return;
                            try {
                              await waiveDriverPolicy(driverId, p.policyDocumentId, reason.trim());
                              load();
                            } catch (e) {
                              setError(e?.response?.data?.message || e?.message || "Waive failed");
                            }
                          }}
                        >
                          Waive
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Activity timeline */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Activity timeline ({activity.length})
          </Typography>
          {activity.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No activity recorded yet.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {activity.map((e, i) => {
                const TYPE_COLOR = {
                  ONBOARDING_STARTED: "#3b82f6",
                  READY_TO_DRIVE: "#16a34a",
                  REJECTED: "#dc2626",
                  TRAINING_ASSIGNED: "#0ea5e9",
                  TRAINING_PASSED: "#16a34a",
                  TRAINING_WAIVED: "#a855f7",
                  TRAINING_EXPIRED: "#dc2626",
                  POLICY_SIGNED: "#16a34a",
                  POLICY_WAIVED: "#a855f7",
                  POLICY_EXPIRED: "#dc2626",
                  DOCUMENT_UPLOADED: "#0ea5e9",
                  DOCUMENT_VERIFIED: "#16a34a",
                };
                const dot = TYPE_COLOR[e.type] || "#475569";
                return (
                  <Box
                    key={`${e.type}-${e.timestamp}-${i}`}
                    sx={{
                      display: "flex",
                      gap: 2,
                      py: 1,
                      borderBottom: i === activity.length - 1 ? "none" : "1px solid #f1f5f9",
                    }}
                  >
                    <Box sx={{ position: "relative", width: 12, flexShrink: 0, mt: 0.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: dot,
                          border: "2px solid white",
                          boxShadow: "0 0 0 1px #e2e8f0",
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {e.summary}
                      </Typography>
                      {e.detail ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {e.detail}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(e.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject driver</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This driver will be marked REJECTED and unable to accept rides. Capture the reason for audit trail.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectReason.trim()}
          >
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DriverCompliancePage;
